import React, { useContext } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { Role } from '@redpanda/types';
import { hasSystemAccess } from '@redpanda/utils';
import { redirectToLogin } from '@redpanda/auth-utils';

interface PrivateRouteProps {
  allowedRoles?: Role[];
  requiredSystem?: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ allowedRoles, requiredSystem = 'HR' }) => {
  const context = useContext(UserContext);

  // 如果正在載入，顯示載入狀態
  if (context?.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">載入中...</p>
        </div>
      </div>
    );
  }

  // 如果未認證，重導向到統一登入中心
  if (!context?.isAuthenticated || !context?.user) {
    const identityServiceUrl = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000';
    redirectToLogin(identityServiceUrl, window.location.href);
    return null;
  }

  // 檢查系統存取權限
  if (requiredSystem && !hasSystemAccess(requiredSystem, context.user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">存取被拒絕</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            您沒有權限存取 {requiredSystem} 系統。
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            請聯繫系統管理員為您的帳號 ({context.user.email}) 分配相應權限。
          </p>
          <button
            onClick={() => context.logout()}
            className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded"
          >
            重新登入
          </button>
        </div>
      </div>
    );
  }

  // 檢查角色權限
  if (allowedRoles && !allowedRoles.includes(context.user.role)) {
    // 角色權限不足，重導向到首頁
    return <Navigate to="/" replace />;
  }

  // 認證和權限檢查都通過，渲染受保護的內容
  return <Outlet />;
};

export default PrivateRoute;
